import React, { useState } from 'react'

import styled from 'styled-components'

import { whatsNewItemsData } from 'src/data/whatsNewItems'
import Footer from 'src/modules/organisms/footer/Footer'
import Navbar from 'src/modules/organisms/navbar/Navbar'
import {
  Body1,
  Paragraph,
  Title,
  TitleH1
} from 'src/components/blocks/Typography'
import Pagination from 'src/components/blog/Pagination'
import { Container } from 'src/components/partials/Container'
import { Grid, Column } from 'src/components/partials/Grid'
import Layout from 'src/components/partials/Layout'
import { ResponsiveSectionWrapper } from 'src/components/partials/Section'
import SEO from 'src/components/partials/Seo'

import { BrandColors } from 'src/utilities/Colors'
import { renderLocalRichText } from 'src/utilities/richTextLocal'
import { WhatsNewTypography } from 'src/utilities/Type'

import { SubscribeHero } from '../../components/resources/NewsletterSignupForms'
import WistiaEmbed from 'src/modules/molecules/wistia-embed'
import Subnavbar from 'src/modules/organisms/subnavbar'

// import { IPostObject } from 'src/utilities/Interfaces'

const ResourceCenterWrapper = styled(ResponsiveSectionWrapper as any)`
  background-color: ${BrandColors.white};
  ${TitleH1} {
    margin-bottom: 1rem;
  }

  ${Body1} {
    margin-bottom: 3rem;
    max-width: 50ch;
  }

  ${Title}, ${Paragraph} {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  ${Title} {
    -webkit-line-clamp: 3;
  }
  ${Paragraph} {
    -webkit-line-clamp: 4;
    margin-top: 1rem;
    margin-bottom: 2rem;
    color: ${BrandColors.pigeon700};
    min-height: 92px;
  }
`

const RichTextWrapper = styled.div`
  ${WhatsNewTypography.toString()}
  pre code {
    line-height: 1.5;
  }

  a {
    color: var(--color-gray-800) !important;
    text-decoration: none !important;
    transition: var(--transition-basic);
    border-bottom: 1px solid var(--color-gray-400) !important;
    cursor: pointer;

    &:hover,
    &:focus,
    &:active {
      color: var(--color-gray-950) !important;
      border-bottom: 1px solid var(--color-gray-950) !important;
    }
  }
`

interface IPostObject {
  id: string
  title: string
  publishedDate: string
  featureImage: {
    file: {
      url: string
    }
  } | null
  featureVideo: {
    video: {
      items: {
        hashed_id: string
      }[]
    }
  } | null
  body: any
}

const AllPosts = ({
  activePage,
  currentPageSet,
  pageChange,
  paginationTotal,
  updatePages
}: {
  activePage: number
  currentPageSet: IPostObject[]
  pageChange: (n: number) => void
  paginationTotal: number
  updatePages: (n: number) => void
}) => {
  const PaginateStyle = styled.div`
    margin: 2.5rem 0;
    .pagination > ul > a > li {
      display: inline-block;
    }
  `

  const changeCurrentPage = numPage => {
    pageChange(numPage)
    updatePages(numPage)
  }

  return (
    <>
      <Container>
        <div className='mb-2 border-b-1 border-gray-300 max-w-container mx-auto'></div>
        {currentPageSet.map(postItem => (
          <React.Fragment key={postItem.id}>
            <div className='py-8 border-b-1 border-gray-300 max-w-container mx-auto'>
              <Grid key={postItem.id} columns={{ md: 9 }} className=''>
                <Column
                  span={{ sm: 3 }}
                  className='flex flex-col justify-between'
                >
                  <div className='mb-0'>
                    {postItem.title && (
                      <h2 className='text-xl mb-1'>{postItem.title}</h2>
                    )}
                    {postItem.publishedDate && (
                      <p className='text-sm'>{postItem.publishedDate}</p>
                    )}
                  </div>
                  {/* <div className='mb-4 flex flex-wrap gap-2'>
                    {postItem.metadata.tags &&
                      postItem.metadata.tags.map(tag => (
                        <div
                          className='px-3 py-1 bg-gray-4 text-sm lowercase'
                          key={tag.id}
                        >
                          {tag.name}
                        </div>
                      ))}
                  </div> */}
                </Column>
                <Column span={{ sm: 6 }}>
                  {postItem.featureVideo && (
                    <div className='mb-8 rounded-sm'>
                      <WistiaEmbed
                        mediaId={
                          postItem.featureVideo?.video?.items[0]?.hashed_id
                        }
                        ariaLabel='Flatfile hero video'
                        aspect='1.7777777777777777'
                      />
                    </div>
                  )}

                  {postItem.featureImage && (
                    <img
                      src={postItem.featureImage?.file?.url}
                      alt={postItem.title}
                      className='w-auto mx-auto block border-1 border-gray-300 mb-8 rounded-sm'
                    />
                  )}

                  <RichTextWrapper>
                    {postItem.body && renderLocalRichText(postItem.body)}
                  </RichTextWrapper>
                </Column>
              </Grid>
            </div>
          </React.Fragment>
        ))}
      </Container>

      <Grid container columns={{ sm: 1 }} columnSpacing={2} rowSpacing={2}>
        <Column>
          <PaginateStyle>
            <Pagination
              changeCurrentPage={changeCurrentPage}
              currentPage={activePage}
              totalPages={paginationTotal}
            />
          </PaginateStyle>
        </Column>
      </Grid>
    </>
  )
}

const WhatsNewPage = () => {
  const postsPerPage = 9

  // Use local data - already sorted by publishedDate desc
  const allPosts = whatsNewItemsData as IPostObject[]
  const [totalPages, setTotalPages] = useState(
    Math.ceil(allPosts.length / postsPerPage)
  )
  const [currentPage, setCurrentPage] = useState(1)
  const [postCards, setPostCards] = useState(
    allPosts.slice(
      currentPage * postsPerPage - postsPerPage,
      currentPage * postsPerPage
    )
  )
  const getPaginationItems = newPage => {
    setPostCards(
      allPosts.slice(
        newPage * postsPerPage - postsPerPage,
        newPage * postsPerPage
      )
    )
  }

  return (
    <Layout>
      <Navbar />
      <Subnavbar />

      <div className='pt-32 md:pt-24 pb-12 md:pb-24'>
        <SubscribeHero
          title='What’s new with Flatfile'
          titleElem='h1'
          description='Read through our latest product updates below and subscribe to our email newsletter to be kept informed about new releases.'
          target='#subscribeModuleForm'
        />
      </div>

      <ResourceCenterWrapper
        paddingSmall='0 0 3rem'
        paddingMedium='0 0 3rem'
        paddingLarge='0 0 3rem'
      >
        {/* display all posts like a blog, each entry showing the title, publishedDate, feature image and rich text body */}
        <AllPosts
          activePage={currentPage}
          currentPageSet={postCards}
          pageChange={setCurrentPage}
          paginationTotal={totalPages}
          updatePages={getPaginationItems}
          // tags={data.whatsNewPosts.edges[0].node.metadata.tags}
        />
      </ResourceCenterWrapper>

      <div className='pt-12 pb-28'>
        <SubscribeHero
          title='Stay up to date with Flatfile <span style="display: block; color: #838383;">product releases</span>'
          titleElem='h3'
          description="Sign up for our email newsletter and we'll make sure you're always kept informed."
          target='#subscribeModuleForm2'
        />
      </div>

      <Footer />
    </Layout>
  )
}

export default WhatsNewPage

export const Head = () => (
  <SEO
    description={`Check out the latest feature releases and updates from Flatfile`}
    title='What’s new with Flatfile'
  />
)
